import { Component, Injector, OnInit, Signal, ViewEncapsulation, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TakeUntilDestroy } from '@shared/decorators/take-until-destroy.decorator';
import { Modal, ModalType, NonPersistedModalName } from '@app/shared/models/modal.model';
import { EventManager } from '@app/shared/utils/event-manager';
import { LoggerService } from '@app/shared/services/logger.service';
import { RankingService } from '@app/shared/services/ranking.service';
import { Ranking } from '@app/shared/models/ranking.model';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { WalletComponent } from '@app/shared/components/wallet/wallet.component';
import { ParagraphComponent } from '@shared/components/text/p/p.component';
import { RankingPrizeComponent } from './ranking-prize/ranking-prize.component';
import { RankingListComponent } from './ranking-list/ranking-list.component';
import { RankingItemComponent } from './ranking-item/ranking-item.component';
import { AvatarComponent } from '@shared/components/avatar/avatar.component';
import { SharedModule } from '@app/shared/shared.module';
import { UserRepository } from '@app/shared/state/user/user.repository';

@TakeUntilDestroy
@Component({
  selector: 'slotcom-ranking-modal',
  templateUrl: './ranking-modal.component.html',
  styleUrls: ['./ranking-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    WalletComponent,
    ParagraphComponent,
    RankingPrizeComponent,
    RankingListComponent,
    RankingItemComponent,
    AvatarComponent,
    SharedModule,
  ],
})
export class RankingModalComponent implements OnInit {
  private rankingService = inject(RankingService);
  private eventManager = inject(EventManager);
  private userRepo = inject(UserRepository);
  logger = inject(LoggerService);
  rankingModalData = inject<Modal>(MAT_DIALOG_DATA);

  isAwardPending = signal(false);

  myCurrentRanking = signal<Ranking | null>(null);
  myLastRanking = signal<Ranking | null>(null);

  topRanking!: Signal<Ranking[]>;
  friendsRanking!: Signal<Ranking[]>;
  lastKingRanking!: Signal<Ranking[]>;
  rankingEnabled = signal(false);

  endDateStr = signal('');
  activeTab = signal(0);

  isFriends = true;

  firstThroneSelf = signal(false);
  secondThroneSelf = signal(false);
  thirdThroneSelf = signal(false);

  logo: string;

  private injector = inject(Injector);

  ngOnInit() {
    this.isEnabled();

    this.getRankings();
    this.getAdditionalInfo();
  }

  setTab(index: number): void {
    this.activeTab.set(index);
  }

  public closeRankingModal() {
    this.eventManager.closeModal(new Modal(NonPersistedModalName.RankingModal, ModalType.View));
  }

  public openRankingInfo() {
    this.eventManager.openModal(new Modal(NonPersistedModalName.RankingInfo, ModalType.View));
  }

  private getRankings(): void {
    this.topRanking = toSignal(this.rankingService.getTopRanking(), { initialValue: [], injector: this.injector });
    this.friendsRanking = toSignal(this.rankingService.getFriendsRanking(), {
      initialValue: [],
      injector: this.injector,
    });
    this.lastKingRanking = toSignal(this.rankingService.getLastTopRanking(1), {
      initialValue: [],
      injector: this.injector,
    });
    this.getMyRanking();
    this.getMyLastRanking();
  }

  private getAdditionalInfo(): void {
    this.isAwardPending.set(this.rankingModalData.data);
    this.getEndDate();
  }

  private getMyLastRanking() {
    this.rankingService.getMyLastRanking(1).subscribe((result: Ranking[]) => {
      this.myLastRanking.set(result[0]);
      this.checkSelfThrones(result[0]);
    });
  }

  private getMyRanking() {
    this.rankingService.getMyRanking().subscribe((result: Ranking[]) => {
      this.myCurrentRanking.set(result.find((r) => r.userId === this.userRepo.getId()));
    });
  }

  private isEnabled(): void {
    this.rankingService.isEnabled().subscribe((enabled: boolean) => {
      this.rankingEnabled.set(enabled);
    });
  }

  private getEndDate(): void {
    const endDate = new Date();
    endDate.setUTCHours(0, 0, 0, 0);
    endDate.setUTCDate(16);
    if (endDate < new Date()) {
      const month = endDate.getUTCMonth();
      endDate.setUTCMonth(month + 1);
    }
    this.endDateStr.set(endDate.toLocaleDateString(this.userRepo.getUser().culture));
    this.endDateStr.set(` ${this.endDateStr().substring(0, this.endDateStr().length - 5)}`);
  }

  private checkSelfThrones(ranking: Ranking) {
    if (!ranking) return;

    this.firstThroneSelf.set(false);
    this.secondThroneSelf.set(false);
    this.thirdThroneSelf.set(false);

    switch (ranking.rankingPosition) {
      case 1:
        this.firstThroneSelf.set(true);
        break;
      case 2:
        this.secondThroneSelf.set(true);
        break;
      case 3:
        this.thirdThroneSelf.set(true);
        break;
    }
  }
}
